// ═══════════════════════════════════════════════════════════════════════════
// FRANKY'S FARM — /abo
// Dedizierte Verkaufsseite für Coffee Pass (19,90€) & Matcha Club (29,90€).
// Ziel: Conversion. Aufgebaut nach Design-Report (Side-by-Side, Sticky-CTA,
// Trust-Strip, FAQ direkt unter Pricing) und Copy-Report (Texte 1:1).
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Shield,
  Pause,
  MapPin,
  Sparkles,
  Coffee,
  Leaf,
  ArrowRight,
} from "lucide-react";
import { useAuth, API } from "@/App";
import { BRAND } from "@/styles/brand";

// ─────────────────────────────────────────────────────────────────────────────
// FARBEN — aus brand.js, nichts hardcoden
// ─────────────────────────────────────────────────────────────────────────────
const C = BRAND.colors;
const COLORS = {
  bg: C.bg,                  // Beige
  bgSoft: C.bgSecondary,     // Cremeweiß
  card: C.bgCard,            // Weiß
  green: C.green,            // Forest Green (Primary)
  greenLight: C.greenLight,
  orange: C.orange,          // Rust Orange (Coffee Akzent)
  orangeLight: C.orangeLight,
  matcha: C.greenLight,      // Heller Grün-Ton für Matcha
  text: C.text,
  textMuted: C.textMuted,
  border: C.border,
  white: C.textLight,
};

const FONT_HEAD = BRAND.typography.heading;
const FONT_BODY = BRAND.typography.body;

// ─────────────────────────────────────────────────────────────────────────────
// PLAN-DATEN — Texte 1:1 aus Copy-Report.
// Plan-IDs ("matcha" / "coffee") matchen Backend (siehe MembershipCheckoutPage).
// ─────────────────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: "coffee",
    name: "Coffee Pass",
    subtitle: "Ein Kaffee pro Tag. Dein Festpreis. Fertig.",
    price: 19.9,
    accentColor: COLORS.orange,
    accentSoft: `${COLORS.orange}15`,
    icon: Coffee,
    iconBg: `${COLORS.orange}20`,
    pricePer: "ab dem 4. Kaffee im Monat zahlt sich's aus",
    bullets: [
      "Such dir täglich einen aus: Espresso, Americano, Cappuccino oder kleiner Latte.",
      "Gilt jeden Tag, auch sonntags — wir sind ja eh da.",
      "Einlösbar in unserer Aachener Filiale, Pontstraße 141.",
      "Keine App. Kein Punktesammeln. Du sagst Hi, wir wissen Bescheid.",
    ],
    guarantee: "3 Monate Mindestlaufzeit. Pausen jederzeit möglich.",
    cta: "Meinen Kaffee starten",
    badge: null,
  },
  {
    id: "matcha",
    name: "Matcha Club",
    subtitle: "Für die, die wissen, was sie wollen.",
    price: 29.9,
    accentColor: COLORS.green,
    accentSoft: `${COLORS.green}12`,
    icon: Leaf,
    iconBg: `${COLORS.green}18`,
    pricePer: "das sind keine 7,50 € pro Matcha",
    bullets: [
      "Hol dir jede Woche einen Basic Matcha — aufs Haus.",
      "Spar 10 % auf alles andere — Cheesecake inklusive.",
      "Einlösbar in unserer Aachener Filiale, Pontstraße 141.",
      "Pausier oder kündige nach 3 Monaten — jederzeit, in 30 Sekunden.",
    ],
    guarantee: "Mindestens 3 Monate dabei. Danach Monat für Monat.",
    cta: "Matcha-Wochen aktivieren",
    badge: "Beliebtester Plan",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// TRUST-STRIP — 4 Mikro-Copy-Zeilen aus Copy-Report (Wortlaut 1:1)
// ─────────────────────────────────────────────────────────────────────────────
const TRUST_ITEMS = [
  { icon: Pause, title: "Pause statt Stress", text: "Urlaub? Krank? Pausier dein Abo, kein Drama." },
  { icon: Shield, title: "Sichere Zahlung", text: "Bezahlt wird über Stripe. Wir sehen deine Karte nie." },
  { icon: MapPin, title: "In Aachen für dich da", text: "Pontstraße 141 — dein Stammplatz." },
  { icon: Sparkles, title: "Keine Sternchen, keine Tricks", text: "Was draufsteht, ist drin." },
];

// ─────────────────────────────────────────────────────────────────────────────
// FAQ — 6 Fragen 1:1 aus Copy-Report
// ─────────────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "Warum 3 Monate Mindestlaufzeit?",
    a: "Ehrliche Antwort: Damit das Abo für uns funktioniert, brauchen wir Planungssicherheit für Bohnen, Matcha und Personal. Drei Monate sind kurz genug, dass du nichts riskierst — und lang genug, dass du das Ritual wirklich aufbaust. Danach läuft's monatlich weiter, kündbar in 30 Sekunden.",
  },
  {
    q: "Wie kündige ich?",
    a: "In deinem Account auf „Mitgliedschaft“ → „Kündigen“ klicken. Keine Hotline, keine Rückhalte-Mail, keine Schuldgefühle. Du bleibst bis zum Ende des bezahlten Monats Mitglied.",
  },
  {
    q: "Kann ich pausieren — z. B. im Urlaub?",
    a: "Ja. Du kannst dein Abo bis zu 4 Wochen am Stück pausieren, beliebig oft im Jahr. Ein Klick im Account, fertig. Während der Pause zahlst du nichts.",
  },
  {
    q: "Wo kann ich einlösen?",
    a: "Aktuell nur in unserer Aachener Filiale (Pontstraße 141). Du sagst beim Bestellen einfach kurz Bescheid („Hey, Coffee Pass“), wir schauen kurz nach und dann läuft's. Kein Vorzeigen, kein QR-Code-Theater. Andere Standorte sind in Planung — sobald es so weit ist, sagen wir Bescheid.",
  },
  {
    q: "Was zählt als „Basic Matcha“?",
    a: "Unser klassischer Matcha Latte mit Milch deiner Wahl (Hafer, Soja, Mandel, Kuh — alles gleich). Spezial-Variationen wie Erdbeer-Matcha, Iced Matcha mit Sirup oder Matcha-Bowls sind extra — gibt's für dich aber mit 10 % Rabatt.",
  },
  {
    q: "Kann ich das verschenken?",
    a: "Noch nicht direkt — ist auf der To-do-Liste. Bis dahin: Schreib uns kurz an hi@frankysfarm.de mit dem Namen vom Beschenkten, wir bauen's manuell. Geht in 24 Stunden.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MINIMAL-HEADER — nur Logo + Login. Keine Navi-Ablenkung.
// ─────────────────────────────────────────────────────────────────────────────
const MinimalHeader = ({ onLogin, isAuthenticated }) => (
  <header
    className="w-full px-4 py-4 flex items-center justify-between"
    style={{ backgroundColor: COLORS.bg, fontFamily: FONT_BODY }}
  >
    <a href="/" className="flex items-center gap-2" aria-label="Zur Startseite">
      <img
        src={BRAND.logo}
        alt="Franky's Farm"
        className="h-8 w-auto"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <span
        className="text-lg font-black"
        style={{ color: COLORS.green, fontFamily: FONT_HEAD }}
      >
        Franky's Farm
      </span>
    </a>
    {!isAuthenticated && (
      <button
        onClick={onLogin}
        className="text-sm font-medium px-3 py-2 rounded-lg"
        style={{ color: COLORS.green, minHeight: 44 }}
      >
        Login
      </button>
    )}
  </header>
);

// ─────────────────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────────────────
const Hero = ({ onScrollToPricing }) => (
  <section
    className="px-5 pt-6 pb-10 md:pt-12 md:pb-16"
    style={{ backgroundColor: COLORS.bg, fontFamily: FONT_BODY }}
  >
    <div className="mx-auto max-w-5xl text-center">
      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight"
        style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
      >
        Komm rein. Dein Kaffee wartet schon.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08 }}
        className="mt-4 mx-auto max-w-2xl text-base sm:text-lg"
        style={{ color: COLORS.textMuted }}
      >
        Zwei Mitgliedschaften für die Menschen, die sowieso jeden Tag bei uns sind —
        und für die, die es ab jetzt sein wollen. Dein Matcha am Mittwoch.
        Dein Cappuccino vor der ersten Mail. Festes Ritual, ehrlicher Preis.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.16 }}
        className="mt-7 flex justify-center"
      >
        <button
          onClick={onScrollToPricing}
          className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl font-bold text-base shadow-lg active:scale-[0.98] transition-transform"
          style={{
            backgroundColor: COLORS.green,
            color: COLORS.white,
            minHeight: 48,
            fontFamily: FONT_HEAD,
            boxShadow: `0 8px 24px ${COLORS.green}33`,
          }}
        >
          Plan wählen
          <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// TRUST-STRIP
// ─────────────────────────────────────────────────────────────────────────────
const TrustStrip = () => (
  <section
    className="px-5 py-6"
    style={{ backgroundColor: COLORS.bgSoft, fontFamily: FONT_BODY }}
    aria-label="Vertrauen und Sicherheit"
  >
    <div className="mx-auto max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-4">
      {TRUST_ITEMS.map(({ icon: Icon, title, text }) => (
        <div key={title} className="flex flex-col items-start gap-2">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${COLORS.green}14` }}
          >
            <Icon className="w-5 h-5" style={{ color: COLORS.green }} />
          </div>
          <p
            className="text-sm font-bold leading-tight"
            style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
          >
            {title}
          </p>
          <p className="text-xs leading-snug" style={{ color: COLORS.textMuted }}>
            {text}
          </p>
        </div>
      ))}
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// PRICING-CARD
// ─────────────────────────────────────────────────────────────────────────────
const PricingCard = ({ plan, onChoose, isLoading }) => {
  const Icon = plan.icon;
  const isFeatured = !!plan.badge;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4 }}
      className="relative flex flex-col rounded-3xl p-6 md:p-7"
      style={{
        backgroundColor: COLORS.card,
        border: isFeatured ? `2px solid ${plan.accentColor}` : `1px solid ${COLORS.border}`,
        boxShadow: isFeatured
          ? `0 16px 40px ${plan.accentColor}25`
          : "0 6px 20px rgba(0,0,0,0.06)",
      }}
    >
      {plan.badge && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap"
          style={{
            backgroundColor: plan.accentColor,
            color: COLORS.white,
            fontFamily: FONT_HEAD,
          }}
        >
          {plan.badge}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: plan.iconBg }}
        >
          <Icon className="w-6 h-6" style={{ color: plan.accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-xl font-black leading-tight"
            style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
          >
            {plan.name}
          </h3>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            {plan.subtitle}
          </p>
        </div>
      </div>

      {/* Preis + Stückkosten */}
      <div
        className="rounded-2xl p-4 mb-5"
        style={{ backgroundColor: plan.accentSoft }}
      >
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-4xl font-black"
            style={{ color: plan.accentColor, fontFamily: FONT_HEAD }}
          >
            {plan.price.toFixed(2).replace(".", ",")} €
          </span>
          <span className="text-sm font-medium" style={{ color: COLORS.textMuted }}>
            / Monat
          </span>
        </div>
        <p
          className="mt-1.5 text-sm italic"
          style={{ color: COLORS.text }}
        >
          {plan.pricePer}.
        </p>
      </div>

      {/* Bullets */}
      <ul className="space-y-3 mb-6 flex-1">
        {plan.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: `${plan.accentColor}20` }}
            >
              <Check className="w-3 h-3" style={{ color: plan.accentColor }} />
            </span>
            <span className="text-sm leading-snug" style={{ color: COLORS.text }}>
              {b}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={() => onChoose(plan)}
        disabled={isLoading}
        className="w-full py-4 rounded-2xl font-bold text-base shadow-md active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          backgroundColor: plan.accentColor,
          color: COLORS.white,
          minHeight: 48,
          fontFamily: FONT_HEAD,
          boxShadow: `0 8px 20px ${plan.accentColor}40`,
        }}
        data-testid={`abo-cta-${plan.id}`}
      >
        {isLoading ? "Einen Moment ..." : plan.cta}
      </button>

      {/* Garantie + FAQ-Link */}
      <p className="mt-3 text-xs text-center" style={{ color: COLORS.textMuted }}>
        {plan.guarantee}{" "}
        <a
          href="#faq"
          className="underline"
          style={{ color: plan.accentColor }}
        >
          Warum?
        </a>
      </p>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRICING-SEKTION
// ─────────────────────────────────────────────────────────────────────────────
const PricingSection = ({ onChoose, loadingPlanId, sectionRef }) => (
  <section
    id="pricing"
    ref={sectionRef}
    className="px-5 py-12 md:py-16"
    style={{ backgroundColor: COLORS.bg, fontFamily: FONT_BODY }}
  >
    <div className="mx-auto max-w-5xl">
      <h2
        className="text-2xl md:text-3xl font-black text-center mb-2"
        style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
      >
        Wähl deinen Pass.
      </h2>
      <p
        className="text-center mb-8 md:mb-10 max-w-xl mx-auto text-sm md:text-base"
        style={{ color: COLORS.textMuted }}
      >
        Trink deinen Kaffee oder Matcha zum Festpreis — in jeder Franky's-Filiale,
        ohne App-Punkte sammeln, ohne Stempelkarten-Voodoo. Ab dem 2. Drink pro Woche
        rechnet's sich. Versprochen.
      </p>

      {/* Side-by-Side Desktop, gestapelt Mobile. Coffee links, Matcha rechts (Anchor). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-3xl mx-auto pt-4">
        {PLANS.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            onChoose={onChoose}
            isLoading={loadingPlanId === plan.id}
          />
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// REFRAME-SEKTION (Mindestlaufzeit beruhigen)
// ─────────────────────────────────────────────────────────────────────────────
const ReframeSection = () => (
  <section
    className="px-5 py-10 md:py-14"
    style={{ backgroundColor: COLORS.bgSoft, fontFamily: FONT_BODY }}
  >
    <div className="mx-auto max-w-2xl text-center">
      <p
        className="text-xs font-bold tracking-widest uppercase mb-3"
        style={{ color: COLORS.green }}
      >
        Mindestlaufzeit, ehrlich
      </p>
      <h2
        className="text-2xl md:text-3xl font-black leading-tight mb-3"
        style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
      >
        3 Monate klingen nach Commitment. Sind aber nur 12 Wochen.
      </h2>
      <p className="text-base leading-relaxed" style={{ color: COLORS.textMuted }}>
        Genug Zeit, dass dein Kaffee zur Gewohnheit wird. Kurz genug, dass du nichts
        riskierst. Du sparst beim Coffee Pass schon ab dem 4. Drink im Monat —
        alles danach ist geschenkt.
      </p>
      <p
        className="mt-5 text-sm italic"
        style={{ color: COLORS.green, fontFamily: FONT_HEAD }}
      >
        3 Monate. Etwa so lang wie eine Netflix-Serie. Nur trinkbar.
      </p>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// SOCIAL-PROOF
// ─────────────────────────────────────────────────────────────────────────────
const Testimonial = ({ author, quote }) => (
  <div
    className="rounded-2xl p-5"
    style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    }}
  >
    <p className="text-sm leading-relaxed mb-3" style={{ color: COLORS.text }}>
      „{quote}“
    </p>
    <p
      className="text-xs font-bold"
      style={{ color: COLORS.green, fontFamily: FONT_HEAD }}
    >
      — {author}
    </p>
  </div>
);

const SocialProofSection = () => (
  <section
    className="px-5 py-12 md:py-16"
    style={{ backgroundColor: COLORS.bg, fontFamily: FONT_BODY }}
  >
    <div className="mx-auto max-w-3xl text-center">
      <p
        className="text-xl md:text-2xl font-black leading-snug max-w-2xl mx-auto"
        style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
      >
        Schon getestet von den Stammgästen, die sowieso jeden Morgen am Fenster sitzen.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
        {/* TODO: echte Testimonials einfügen — diese 2 sind Platzhalter */}
        <Testimonial
          author="Lisa, Aachen"
          quote="Ich war eh jeden Morgen vor der Vorlesung da. Jetzt fühlt sich's an, als hätte ich nen Stammplatz."
        />
        <Testimonial
          author="Jonas, Aachen"
          quote="Ich hab's mal kurz ausgerechnet — ab Woche 2 spar ich. Plus, der Cappuccino ist eh besser als überall sonst."
        />
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────────────────────
const FaqItem = ({ q, a, isOpen, onToggle }) => (
  <div
    className="rounded-2xl overflow-hidden"
    style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
    }}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left p-4 md:p-5"
      style={{ minHeight: 48, fontFamily: FONT_BODY }}
      aria-expanded={isOpen}
    >
      <span
        className="text-sm md:text-base font-bold pr-3"
        style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
      >
        {q}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.green }} />
      ) : (
        <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.textMuted }} />
      )}
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22 }}
        >
          <div className="px-4 md:px-5 pb-5">
            <p className="text-sm leading-relaxed" style={{ color: COLORS.textMuted }}>
              {a}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FaqSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faq"
      className="px-5 py-12 md:py-16"
      style={{ backgroundColor: COLORS.bgSoft, fontFamily: FONT_BODY }}
    >
      <div className="mx-auto max-w-3xl">
        <h2
          className="text-2xl md:text-3xl font-black text-center mb-2"
          style={{ color: COLORS.text, fontFamily: FONT_HEAD }}
        >
          Ehrliche Fragen, ehrliche Antworten.
        </h2>
        <p
          className="text-center mb-8 text-sm"
          style={{ color: COLORS.textMuted }}
        >
          Was Stammgäste vor dem Klick noch wissen wollen.
        </p>

        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <FaqItem
              key={i}
              q={faq.q}
              a={faq.a}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FINAL-CTA
// ─────────────────────────────────────────────────────────────────────────────
const FinalCta = ({ onChoose, loadingPlanId }) => (
  <section
    className="px-5 py-14 md:py-20"
    style={{ backgroundColor: COLORS.green, fontFamily: FONT_BODY }}
  >
    <div className="mx-auto max-w-3xl text-center">
      <h2
        className="text-2xl md:text-4xl font-black leading-tight"
        style={{ color: COLORS.white, fontFamily: FONT_HEAD }}
      >
        Mach's zur Gewohnheit. Wir machen den Rest.
      </h2>
      <p className="mt-4 text-base md:text-lg" style={{ color: "#ffffffcc" }}>
        Festpreis in Aachen. Pause statt Stress. Kündigen in 30 Sekunden.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        {PLANS.map((plan) => (
          <button
            key={plan.id}
            onClick={() => onChoose(plan)}
            disabled={loadingPlanId === plan.id}
            className="px-6 py-4 rounded-2xl font-bold text-base shadow-lg active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{
              backgroundColor: COLORS.white,
              color: plan.accentColor,
              minHeight: 48,
              fontFamily: FONT_HEAD,
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
            }}
          >
            {loadingPlanId === plan.id ? "Einen Moment ..." : `${plan.cta} – ${plan.price.toFixed(2).replace(".", ",")} €`}
          </button>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// STICKY MOBILE-CTA
// Verschwindet sobald Pricing-Sektion sichtbar ist.
// ─────────────────────────────────────────────────────────────────────────────
const StickyMobileCta = ({ onScrollToPricing, hidden }) => (
  <AnimatePresence>
    {!hidden && (
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="md:hidden fixed left-0 right-0 z-40 px-4"
        style={{
          bottom: 0,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          paddingTop: 12,
          background: `linear-gradient(180deg, ${COLORS.bg}00 0%, ${COLORS.bg} 30%)`,
        }}
      >
        <button
          onClick={onScrollToPricing}
          className="w-full py-4 rounded-2xl font-bold text-base shadow-2xl active:scale-[0.98] transition-transform"
          style={{
            backgroundColor: COLORS.green,
            color: COLORS.white,
            minHeight: 52,
            fontFamily: FONT_HEAD,
            boxShadow: `0 12px 28px ${COLORS.green}55`,
          }}
        >
          Pass holen — ab 19,90 €
        </button>
      </motion.div>
    )}
  </AnimatePresence>
);

// ═══════════════════════════════════════════════════════════════════════════
// HAUPT-PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function AboPage() {
  const navigate = useNavigate();
  const { user, token, setShowAuthModal } = useAuth();

  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [pricingVisible, setPricingVisible] = useState(false);
  const pricingRef = useRef(null);

  // Sticky-CTA ausblenden, sobald Pricing-Sektion im Viewport
  useEffect(() => {
    if (!pricingRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPricingVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToPricing = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Plan wählen → Auth-Check → Stripe-Checkout starten
  const handleChoose = async (plan) => {
    if (!user || !token) {
      // Redirect-Param hängen wir an, damit nach Login zurück auf /abo
      try {
        sessionStorage.setItem("abo_pending_plan", plan.id);
      } catch (e) {}
      // App nutzt Modal-basierten Auth — wir öffnen es. Falls Login-Page
      // bevorzugt: navigate(`/login?redirect=/abo`); — bewusst Modal,
      // weil ?redirect-Logik im bestehenden AuthModal noch nicht sauber.
      setShowAuthModal(true);
      toast.info("Kurz einloggen, dann geht's los.");
      return;
    }

    setLoadingPlanId(plan.id);
    try {
      const res = await axios.post(
        `${API}/membership/checkout`,
        { plan_id: plan.id, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("Keine Checkout-URL erhalten");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(
        err?.response?.data?.detail ||
          "Hat gerade nicht geklappt. Probier's gleich nochmal."
      );
      setLoadingPlanId(null);
    }
  };

  // Falls User nach Login zurückkommt und vorher einen Plan gewählt hatte:
  // direkt Checkout fortsetzen.
  useEffect(() => {
    if (user && token) {
      try {
        const pending = sessionStorage.getItem("abo_pending_plan");
        if (pending) {
          sessionStorage.removeItem("abo_pending_plan");
          const plan = PLANS.find((p) => p.id === pending);
          if (plan) handleChoose(plan);
        }
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: COLORS.bg, fontFamily: FONT_BODY }}
    >
      <MinimalHeader
        onLogin={() => setShowAuthModal(true)}
        isAuthenticated={!!user}
      />

      <main>
        <Hero onScrollToPricing={scrollToPricing} />
        <TrustStrip />
        <PricingSection
          onChoose={handleChoose}
          loadingPlanId={loadingPlanId}
          sectionRef={pricingRef}
        />
        <ReframeSection />
        <SocialProofSection />
        <FaqSection />
        <FinalCta onChoose={handleChoose} loadingPlanId={loadingPlanId} />
      </main>

      <StickyMobileCta
        onScrollToPricing={scrollToPricing}
        hidden={pricingVisible}
      />

      {/* Bottom-Spacer damit Sticky-CTA nichts überdeckt */}
      <div className="md:hidden" style={{ height: 80 }} aria-hidden="true" />
    </div>
  );
}
